import { call, put, takeEvery, select } from 'redux-saga/effects';
import listReducer from 'src/components/filters/reducers/api';
import queryString from 'querystring';
import isNumeric from '../../../globals/helpers/is-numeric';

import {
  COMPARE_PROPERTY,
  FILTER_CHANGE_ACTION,
  filterQuery,
  filterReceive,
  filterFetchFailed,
  compareProperties,
} from '../actions/filter';

const reducer = (query, action) => {
  switch (action.filterType) {
    case 'boolean': {
      if (action.value === null) {
        // eslint-disable-next-line no-param-reassign
        delete query[action.id];
        return {
          ...query,
        };
      }
      return {
        ...query,
        [action.id]: action.value,
      };
    }
    case 'slider': {
      if (action.value === null) {
        // eslint-disable-next-line no-param-reassign
        delete query[action.id];
        return {
          ...query,
        };
      }
      return {
        ...query,
        [action.id]: action.value,
      };
    }
    case 'list': {
      return listReducer(query, action);
    }
    case 'string': {
      return {
        ...query,
        [action.id]: action.value,
      };
    }
    default: {
      return query;
    }
  }
};

const getQuery = state => state.areas.category.filter.query;
const getCompareProperties = state => state.areas.category.filter.comparing;
const getLoadedCategoryId = state =>
  state.areas.category.filter.loadedCategoryId;

const getFilter = (code, id, items) =>
  fetch(`/_internal/filters/${id}${items ? `/${items}` : ''}`, {
    credentials: 'include',
  })
    .then(res => {
      if (res.status !== 200) {
        throw new Error(
          `Filter fetch failed for ID ${id}${items ? `, Items ${items}` : ''}`
        );
      }

      return res.json();
    })
    .catch(err => ({ error: err }));

const postFilter = (code, id, query) =>
  fetch(`/_internal/filters/${id}`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(query),
  })
    .then(res => {
      if (res.status !== 200) {
        throw new Error(`Filter create failed for ID ${id}`);
      }

      return res.json();
    })
    .then(data => data.items[0])
    .catch(err => ({ error: err }));

// Utility to get certain properties from an object as a new object
const pickProperties = (props, obj) =>
  Object.keys(obj).reduce(
    (params, key) =>
      props.includes(key) ? Object.assign(params, { [key]: obj[key] }) : params,
    {}
  );

export function* handleFilterActions(action) {
  switch (action.type) {
    case 'ROUTE_LOADED': {
      if (!action.route) {
        break;
      }
      const qs = queryString.decode(location.search.substr(1));

      if (qs.compare) {
        yield put(compareProperties(qs.compare.split(',').filter(isNumeric)));
      }

      const loadedCategoryId = yield select(getLoadedCategoryId);

      if (
        action.route.name !== 'category' ||
        !action.data ||
        !action.data.category
      ) {
        break;
      }

      const categoryState = action.data.category;

      const { id, filterId, immediateProducts, brand, productCount } =
        action.category || categoryState;

      const categoryData = {
        categoryId: id,
        productCount,
      };

      if (!immediateProducts) {
        break;
      }

      const toggledBrandCategory = yield select(
        state => state.areas.category.filter.toggledBrandCategory
      );
      const hasChangedCategory =
        loadedCategoryId !== id ||
        (loadedCategoryId === id && toggledBrandCategory);

      if (hasChangedCategory) {
        let brandFilterId = null;

        // If category is filtered on brand. Create a new filter search
        if (brand) {
          const brandFilter = yield call(
            postFilter,
            window.CLIENT_MARKET.code,
            id,
            {
              brands: [String(brand.id)],
            }
          );
          brandFilterId = brandFilter.id;
        }

        const data = yield call(
          getFilter,
          window.CLIENT_MARKET.code,
          brandFilterId || filterId || id
        );

        if (data.error) {
          yield put(filterFetchFailed(data, id));
        } else {
          yield put(filterReceive(data, categoryData));
        }
      }
      break;
    }

    case FILTER_CHANGE_ACTION: {
      const { useClassicUrls } = window.CLIENT_MARKET;

      const { replace } = window.__history; // No idea why this is undefined in scope above

      const query = yield select(getQuery);
      const newQuery = reducer(query, action);

      yield put(filterQuery(newQuery));

      const categoryId = action.categoryId;

      const newFilter = yield call(
        postFilter,
        window.CLIENT_MARKET.code,
        categoryId,
        newQuery
      );

      if (newFilter.error) {
        yield put(filterFetchFailed({ error: newFilter.error }, categoryId));
        return;
      }

      const filterId = newFilter.id;

      const qs = queryString.decode(location.search.substr(1));
      const paramsToPropagate = pickProperties(
        [
          'brand',
          't',
          'sort',
          'k',
          'b',
          'm',
          'o',
          'catId',
          'compare',
          'direction',
        ],
        qs
      );

      // TODO: Move this to a side effect somehow
      if (Object.keys(newQuery).length === 0) {
        if (useClassicUrls) {
          paramsToPropagate.k = newFilter.parent.id;
          delete paramsToPropagate.catId;
        }

        yield call(
          replace,
          `${window.location.pathname}?${queryString.encode(paramsToPropagate)}`
        );
      } else {
        const newQueryString = paramsToPropagate;

        if (useClassicUrls) {
          newQueryString.catId = categoryId;
          newQueryString.k = filterId;
        } else {
          newQueryString.filterId = filterId;
        }

        yield call(
          replace,
          `${window.location.pathname}?${queryString.encode(newQueryString)}`
        );
      }

      const data = yield call(getFilter, window.CLIENT_MARKET.code, filterId);

      if (data.error) {
        yield put(filterFetchFailed(data, categoryId));
      } else {
        yield put(filterReceive(data, { categoryId }));
      }

      break;
    }
    case COMPARE_PROPERTY: {
      const { replace } = window.__history; // No idea why this is undefined in scope above
      const compare = yield select(getCompareProperties);

      const selectedProps = compare.filter(isNumeric);

      const currentParams = queryString.decode(
        window.location.search.substring(1)
      );

      const newQueryString = {
        ...currentParams,
        compare: selectedProps.join(','),
      };

      yield call(
        replace,
        `${window.location.pathname}?${queryString.encode(newQueryString)}`
      );

      break;
    }
    default: {
      break;
    }
  }
}

function* mySaga() {
  yield takeEvery(
    ['ROUTE_LOADED', FILTER_CHANGE_ACTION, COMPARE_PROPERTY],
    handleFilterActions
  );
}

export default mySaga;
